import { useLocation } from 'react-router-dom';

/**
 * Where the guest-discovery pages' back link returns to.
 *
 * A static-site page that links into /discover can tag the link with
 * `?from=<key>`. The origin is remembered at module scope, so it survives
 * client-side moves between /discover, /discover/:slug and /discover/choose,
 * and resets on the next full page load (a later untagged visit from /demo
 * gets the default again). Only allowlisted keys are honoured, so the param
 * can't point the back link anywhere off-site.
 *
 * Every page a tagged link can land on (/discover, /discover/:slug,
 * /discover/choose) calls useDiscoveryOrigin(), so a visitor who enters on a
 * category chip still gets the right back link once they reach /discover.
 */
const ORIGINS = {
  // #try: the section the discovery links sit in, so "back" lands there.
  'umuzi-partners': { href: '/for/umuzi-partners#try', label: 'Praxis' },
};

// The static pages an origin points at (path only), so a conversation's exit
// can leave the SPA for them with a full page load (staticSiteExit.js).
export const DISCOVERY_ORIGIN_PATHS = Object.values(ORIGINS).map((o) => o.href.split('#')[0]);
const DEFAULT_ORIGIN = { href: '/demo', label: 'Tacit' };

let remembered = null;

export function resolveDiscoveryOrigin(search = '') {
  const key = new URLSearchParams(search).get('from');
  if (key && Object.prototype.hasOwnProperty.call(ORIGINS, key)) remembered = ORIGINS[key];
  return remembered || DEFAULT_ORIGIN;
}

// The remembered tagged origin's href, or null when the visitor did not
// arrive through a tagged link. ChatPage uses it as the guest's exit, so a
// guest who came from /for/umuzi-partners goes back there, not to /demo.
// Module memory survives the client-side move into /chat/:runId; a full
// reload of the chat page loses it and the exit falls back to /demo.
export function rememberedDiscoveryOrigin() {
  return remembered ? remembered.href : null;
}

export function useDiscoveryOrigin() {
  return resolveDiscoveryOrigin(useLocation().search);
}

// Test-only: module state otherwise leaks between test cases.
export function resetDiscoveryOriginForTests() {
  remembered = null;
}
